import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  GeneratorCategory,
  PowerType,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { TFunction } from 'i18next'
import { z } from 'zod'

import {
  type CivilDate,
  compareCivilDate,
  todayCivilDate,
} from '~/models/civilDate'
import { withinRunes } from '~/pages/AdminOcctoMaster/tabs/_common/schema'
import type { PlantUnitRow } from '~/pages/AdminPlantUnitMaster/models/unitRows'

/**
 * 入力の文字数上限 (docs/domain/models/plant-unit.md)。前後の空白を除いた文字数で数える。
 * 欄ごとに `N / 上限` を出し、超過は保存時の文言で止める (入力中は削らない)。
 */
export const UNIT_NAME_MAX_LENGTH = 100
export const UNIT_NAME_ABBR_MAX_LENGTH = 50
export const POWER_SOURCE_ID_MAX_LENGTH = 20
export const GENERATOR_IDENTIFIER_MAX_LENGTH = 21
export const UNIT_NOTE_MAX_LENGTH = 100

/** proto int64 の上限 (認可出力の kW)。 */
const MAX_INT64 = BigInt('9223372036854775807')

/** 登録ダイアログでその場で「発電所を追加」した、まだ保存していない発電所を指す仮の id。 */
export const PENDING_SITE_ID = '__pending-site__'

const civilDateObject = z.object({
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  day: z.number().int().min(1).max(31),
})

/** 任意の日付 (未入力は undefined / null)。 */
const optionalCivilDateSchema = civilDateObject.nullable().optional()

/** 必須の日付。未入力の文言は欄ごとに変える (出力は常に値あり)。 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- zod の合成型は長く、推論に任せる
const requiredCivilDateSchema = (message: string) =>
  optionalCivilDateSchema.transform((value, ctx) => {
    if (value === null || value === undefined) {
      ctx.addIssue({ code: 'custom', message })
      return z.NEVER
    }
    return value
  })

/**
 * 電源ユニットの登録・編集フォームの schema。
 *
 * - 必須: 発電所・ユニット名・略称・発電形式・電源種別・認可出力・稼働開始日・有効開始日
 * - ユニット名・略称は前後の空白を除いた値を扱う (backend の entity も同じ)
 * - `areaCode` は発電所・系統コードの候補を絞るだけの欄で、保存しない
 * - 発電所内でのユニット名の一意は backend が保存時に判定する (AlreadyExists)
 */
// [規約差異] schemaは表示言語の自然文を返す。日本語はja辞書、英語はspec付録を正とする。
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- zodの合成型は推論に任せる
export const buildPlantUnitFormSchema = (t: TFunction) =>
  z
    .object({
      siteId: z
        .string()
        .min(1, t('page:adminPlantUnitMaster.unit.validation.siteRequired')),
      areaCode: z.nativeEnum(AreaCode),
      plantMasterId: z.string(),
      unitName: z
        .string()
        .trim()
        .min(1, t('page:adminPlantUnitMaster.unit.validation.unitNameRequired'))
        .refine(
          withinRunes(UNIT_NAME_MAX_LENGTH),
          t('page:adminPlantUnitMaster.unit.validation.unitNameTooLong'),
        ),
      unitNameAbbr: z
        .string()
        .trim()
        .min(
          1,
          t('page:adminPlantUnitMaster.unit.validation.unitNameAbbrRequired'),
        )
        .refine(
          withinRunes(UNIT_NAME_ABBR_MAX_LENGTH),
          t('page:adminPlantUnitMaster.unit.validation.unitNameAbbrTooLong'),
        ),
      // proto は not_in: [0] を課すため UNSPECIFIED を弾く (nativeEnum だけでは 0 が通る)。
      generatorCategory: z
        .nativeEnum(GeneratorCategory, {
          error: () =>
            t(
              'page:adminPlantUnitMaster.unit.validation.generatorCategoryRequired',
            ),
        })
        .refine(
          (v) => v !== GeneratorCategory.UNSPECIFIED,
          t(
            'page:adminPlantUnitMaster.unit.validation.generatorCategoryRequired',
          ),
        ),
      // 6 択 (FIT特例②を含む)。未指定は選べない。
      powerType: z
        .nativeEnum(PowerType, {
          error: () =>
            t('page:adminPlantUnitMaster.unit.validation.powerTypeRequired'),
        })
        .refine(
          (v) => v !== PowerType.UNSPECIFIED,
          t('page:adminPlantUnitMaster.unit.validation.powerTypeRequired'),
        ),
      authorizedOutputKw: z
        .string()
        .trim()
        .min(
          1,
          t(
            'page:adminPlantUnitMaster.unit.validation.authorizedOutputRequired',
          ),
        )
        .refine(
          (v) => /^-?\d+$/.test(v),
          t(
            'page:adminPlantUnitMaster.unit.validation.authorizedOutputInteger',
          ),
        )
        .refine(
          (v) => !/^-\d+$/.test(v) || BigInt(v) >= BigInt(0),
          t(
            'page:adminPlantUnitMaster.unit.validation.authorizedOutputNonnegative',
          ),
        )
        .refine(
          (v) => !/^-?\d+$/.test(v) || BigInt(v) <= MAX_INT64,
          t(
            'page:adminPlantUnitMaster.unit.validation.authorizedOutputTooLarge',
          ),
        ),
      receivingPointId: z
        .string()
        .trim()
        .refine(
          (v) => v === '' || /^\d{22}$/.test(v),
          t('page:adminPlantUnitMaster.unit.validation.receivingPointId'),
        ),
      powerSourceId: z
        .string()
        .trim()
        .refine(
          withinRunes(POWER_SOURCE_ID_MAX_LENGTH),
          t('page:adminPlantUnitMaster.unit.validation.powerSourceIdTooLong'),
        ),
      generatorIdentifier: z
        .string()
        .trim()
        .refine(
          withinRunes(GENERATOR_IDENTIFIER_MAX_LENGTH),
          t('page:adminPlantUnitMaster.unit.validation.generatorIdentifier'),
        ),
      operationStartDate: requiredCivilDateSchema(
        t('page:adminPlantUnitMaster.unit.validation.operationStartRequired'),
      ),
      operationEndDate: optionalCivilDateSchema,
      validFrom: requiredCivilDateSchema(
        t('page:adminPlantUnitMaster.unit.validation.validFromRequired'),
      ),
      validTo: optionalCivilDateSchema,
      note: z
        .string()
        .trim()
        .refine(
          withinRunes(UNIT_NOTE_MAX_LENGTH),
          t('page:adminPlantUnitMaster.unit.validation.noteTooLong'),
        ),
    })
    // 稼働開始日 ≦ 稼働終了日
    .refine(
      (values) =>
        values.operationEndDate === null ||
        values.operationEndDate === undefined ||
        compareCivilDate(values.operationStartDate, values.operationEndDate) <=
          0,
      {
        message: t(
          'page:adminPlantUnitMaster.unit.validation.operationEndOrder',
        ),
        path: ['operationEndDate'],
      },
    )
    // 有効開始日 ≦ 有効終了日
    .refine(
      (values) =>
        values.validTo === null ||
        values.validTo === undefined ||
        compareCivilDate(values.validFrom, values.validTo) <= 0,
      {
        message: t('page:adminPlantUnitMaster.unit.validation.validToOrder'),
        path: ['validTo'],
      },
    )

export type PlantUnitFormInput = z.input<
  ReturnType<typeof buildPlantUnitFormSchema>
>
export type PlantUnitFormValues = z.output<
  ReturnType<typeof buildPlantUnitFormSchema>
>

/**
 * 他のユニットとの突き合わせ (入力中の E1)。入力が問題なければ undefined、あれば電源種別の欄に出す文言。
 * 一覧を持つ画面 (登録ダイアログ・詳細ページ) が渡す。入力 (検証前の値) を見る。
 */
export type PlantUnitCrossCheck = (
  values: PlantUnitFormInput,
) => string | undefined

/**
 * `buildPlantUnitFormSchema` に、他のユニットとの突き合わせ (電源種別の欄のエラー) を足した schema。
 * 他の欄が未入力でも突き合わせは行い (系統コード・電源種別・有効期間が入った時点で知らせるため)、
 * 両方の指摘を 1 つにまとめて返す。
 */
export function buildPlantUnitFormSchemaWithCrossCheck(
  t: TFunction,
  crossCheck: PlantUnitCrossCheck,
): z.ZodType<PlantUnitFormValues, PlantUnitFormInput> {
  const baseSchema = buildPlantUnitFormSchema(t)
  return z.unknown().transform((input, ctx) => {
    const parsed = baseSchema.safeParse(input)
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        ctx.addIssue(issue as never)
      }
    }
    const message = crossCheck(input as PlantUnitFormInput)
    if (message !== undefined) {
      ctx.addIssue({ code: 'custom', message, path: ['powerType'] })
    }
    return parsed.success && message === undefined ? parsed.data : z.NEVER
  }) as unknown as z.ZodType<PlantUnitFormValues, PlantUnitFormInput>
}

/**
 * 新規登録の初期値。有効開始日は今日、エリア (絞り込み用) は一覧の絞り込みを引き継がず常に未選択
 * (エリアを選ぶまで発電所・系統コードは選べない)。発電形式・電源種別は未選択、認可出力は未入力で、
 * 保存時に選ぶ / 入れるまで止める。
 */
export function defaultPlantUnitFormInput(
  today: CivilDate = todayCivilDate(),
): PlantUnitFormInput {
  return {
    siteId: '',
    areaCode: AreaCode.UNSPECIFIED,
    plantMasterId: '',
    unitName: '',
    unitNameAbbr: '',
    generatorCategory: GeneratorCategory.UNSPECIFIED,
    powerType: PowerType.UNSPECIFIED,
    authorizedOutputKw: '',
    receivingPointId: '',
    powerSourceId: '',
    generatorIdentifier: '',
    operationStartDate: undefined,
    operationEndDate: undefined,
    validFrom: today,
    validTo: undefined,
    note: '',
  }
}

/** 既存のユニットで form を埋める。エリアは絞り込み用なので、ユニットのエリアを入れておく。 */
export function plantUnitRowToFormInput(row: PlantUnitRow): PlantUnitFormInput {
  return {
    siteId: row.generationSiteId,
    areaCode: row.areaCode,
    plantMasterId: row.plantMasterId ?? '',
    unitName: row.unitName,
    unitNameAbbr: row.unitNameAbbr,
    generatorCategory: row.generatorCategory,
    powerType: row.powerType,
    authorizedOutputKw: String(row.authorizedOutputKw),
    receivingPointId: row.receivingPointId,
    powerSourceId: row.powerSourceId,
    generatorIdentifier: row.generatorIdentifier,
    operationStartDate: row.operationStartDate,
    operationEndDate: row.operationEndDate,
    validFrom: row.validFrom,
    validTo: row.validTo,
    note: row.note,
  }
}

function toOptionalText(value: string): string | undefined {
  return value === '' ? undefined : value
}

/** Create / Update の request に共通するユニットの属性。未入力の任意項目は送らない (absent)。 */
export function plantUnitAttributesFromValues(values: PlantUnitFormValues): {
  plantMasterId: string | undefined
  unitName: string
  unitNameAbbr: string
  generatorCategory: GeneratorCategory
  powerType: PowerType
  authorizedOutputKw: bigint
  receivingPointId: string | undefined
  powerSourceId: string | undefined
  generatorIdentifier: string | undefined
  operationStartDate: CivilDate
  operationEndDate: CivilDate | undefined
  validFrom: CivilDate
  validTo: CivilDate | undefined
  note: string | undefined
} {
  return {
    plantMasterId: toOptionalText(values.plantMasterId),
    unitName: values.unitName,
    unitNameAbbr: values.unitNameAbbr,
    generatorCategory: values.generatorCategory,
    powerType: values.powerType,
    authorizedOutputKw: BigInt(values.authorizedOutputKw),
    receivingPointId: toOptionalText(values.receivingPointId),
    powerSourceId: toOptionalText(values.powerSourceId),
    generatorIdentifier: toOptionalText(values.generatorIdentifier),
    operationStartDate: values.operationStartDate,
    operationEndDate: values.operationEndDate ?? undefined,
    validFrom: values.validFrom,
    validTo: values.validTo ?? undefined,
    note: toOptionalText(values.note),
  }
}
